import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch } from 'react-redux';

import { trackPinClick } from './trackPinClick';
import { useReloadHybridSearchAfterMapInteraction } from './useReloadHybridSearchAfterMapInteraction';
import { closeMobileListing } from '../../../../common/actions/listingModal';
import { useApplicationContext } from '../../../../common/containers/ApplicationContext';
import { IHotspotProperties } from '../../../../common/types/map';
import { TThunkDispatch } from '../../../../common/types/redux';
import { precisionToMinZoom, zoomToPrecision } from '../../../../common/utils/precision';
import { useMapController } from '../../../controller/useMapController';
import { MAP_SET_CENTER_OPTIONS } from '../constants';

interface IUseHotspotClickHandlerParams {
  isResultsAvailable: boolean;
  isDetailsVisible: boolean;
}

export function useHotspotClickHandler({
  isResultsAvailable,
  isDetailsVisible,
}: IUseHotspotClickHandlerParams): (event: YMaps.IEvent) => void {
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const { config } = useApplicationContext();
  const controller = useMapController();
  const reloadHybridSearchAfterMapInteraction = useReloadHybridSearchAfterMapInteraction();

  return React.useCallback(
    (event: YMaps.IEvent) => {
      const currentZoom = controller.getZoom();
      const [, maxPossibleZoom] = controller.getZoomRange();
      const activeObject = event.get<YMaps.IHotspotLayerObject<IHotspotProperties>>('activeObject');
      const { originalFeature } = activeObject.getProperties();

      trackPinClick({
        isDetailsVisible,
        zoom: Math.round(currentZoom),
      });

      if (!isResultsAvailable && currentZoom <= maxPossibleZoom) {
        const currentPrecision = zoomToPrecision(config, currentZoom);
        const nextPrecision = currentPrecision + 1;
        const nextPrecisionZoom = precisionToMinZoom(config, nextPrecision);
        const nextFallbackZoom = currentZoom + 2;
        const coords = originalFeature.geometry.coordinates;

        const nextZoom = Math.min(nextPrecisionZoom || nextFallbackZoom, maxPossibleZoom);
        const center: YMaps.TCoord = [coords[0], coords[1]];

        // closeMobileListing - мобильная концепция: сбрасывает офферы общего листинга.
        // На десктопе листинг живёт и его трогать нельзя, иначе клик по веснушке чистит выдачу.
        if (deviceType !== 'desktop') {
          dispatch(closeMobileListing());
        }
        // focusOn - программное движение, поэтому actionend/onUserIntent его не подхватит. Данные
        // для нового зума догружаем сами в onApplied (после перелёта) тем же reload-сценарием, что жесты/кнопки.
        controller.focusOn({
          center,
          zoom: nextZoom,
          options: MAP_SET_CENTER_OPTIONS,
          onApplied: () => reloadHybridSearchAfterMapInteraction({ mapSnapshot: controller.getSnapshot() }),
        });

        return;
      }
    },
    [
      controller,
      isResultsAvailable,
      isDetailsVisible,
      config,
      dispatch,
      deviceType,
      reloadHybridSearchAfterMapInteraction,
    ],
  );
}
